/**
 * Listagem de agendamentos em cards (HNS-1594).
 *
 * A tabela desta tela acumulou até cinco botões por linha, e no perfil do prestador —
 * quem mais usa a tela — todos competem entre si. Aqui cada agendamento é um card com
 * hierarquia clara: quando é, com quem, o que é, e UMA ação em destaque; o resto no menu.
 *
 * O visual segue a proposta do Claude Design (projeto "Redesign de cards de agendamento"),
 * cujas três decisões estruturais estão comentadas no bloco `.ag-card` do index.css:
 * o grid sai do container branco, o card troca borda de 2px por elevação, e o rodapé vai
 * para a base por `margin-top:auto` em vez de ser separado por `<hr>`.
 *
 * É só apresentação. Todas as ações continuam vindo por callback de Agendamentos.jsx, que
 * segue sendo a dona das regras e dos modais — as duas visões não podem divergir.
 */
import React from "react";
import { Dropdown } from "react-bootstrap";
import {
    FaRegEdit, FaEye, FaFile, FaRegCalendarAlt, FaVideo, FaBoxOpen, FaClipboardList, FaChevronDown,
} from "react-icons/fa";
import { formataDataExibicao, situacoesAgendamento, agendamentoEncerrado } from "../../common/utils";
import { semData } from "../Atendimentos/FluxoIntegrado/tokens";
import AcoesLinkPaciente from "../Meetings/AcoesLinkPaciente";

const DIAS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

// A barra da esquerda repete a cor do status: ela é o que se lê antes de qualquer
// texto quando a tela tem muitos cards. Mesma paleta das outras telas.
const CORES_BARRA = {
    liberado: "rgb(94,116,251)",
    agendado: "rgb(94,116,251)",
    solicitado: "rgb(255,193,7)",
    arquivo_pendente: "rgb(255,193,7)",
    em_analise: "#c3cdd9",
    finalizado: "#d3dce6",
    cancelado: "#d3dce6",
};

const partesData = (valor) => {
    if (semData(valor)) return null;
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return null;
    return {
        semana: DIAS[d.getDay()],
        dia: String(d.getDate()).padStart(2, "0"),
        mes: MESES[d.getMonth()],
        ano: d.getFullYear(),
        hora: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
    };
};

const resumoKit = (item) => {
    if (!item.atendimento_kit_id) return null;
    const kit = item.dados_atendimento_kit || {};
    if (kit.status === "cancelado") return "Kit cancelado";
    if (kit.data_envio_realizado) return `Kit enviado em ${formataDataExibicao(kit.data_envio_realizado)}`;
    if (kit.data_envio) return `Kit previsto para ${formataDataExibicao(kit.data_envio)}`;
    return "Kit a enviar";
};

const CardsAgendamentos = ({
    itens = [],
    dadosUsuario = {},
    podeEditar = () => false,
    podeInformarData = () => false,
    ehAlteracaoDeData = () => false,
    mostrarFormularioDinamico = () => false,
    tituloFormularioDinamico = () => "Formulário do serviço",
    podeAnalisarArquivos = () => false,
    onConfirmarRealizacao,
    onInformarData,
    onFormularioDinamico,
    onArquivos,
    onConfirmarEnvioKit,
}) => {
    const perfil = dadosUsuario["tipo_usuario"];
    const ehPrestador = perfil === "prestador";
    const ehBeneficiario = perfil === "beneficiario";

    if (itens.length === 0) {
        return (
            <div style={{ padding: "28px 4px", fontSize: "13px", color: "#6b7a8d" }}>
                Nenhum agendamento encontrado.
            </div>
        );
    }

    return (
        <div className="ag-grid">
            {itens.map((item) => {
                const encerrado = agendamentoEncerrado(item.situacao);
                const data = partesData(item.data_agendamento);
                const kitPendente = item.atendimento_kit_id && item.dados_atendimento_kit?.status === "agendado";
                const kit = resumoKit(item);
                const sala = item.dados_meeting?.whereby_sala_url;
                const podeEntrarNaSala = !encerrado && (ehPrestador || ehBeneficiario)
                    && item.dados_meeting?.whereby_sala_id && sala;
                const emAberto = !["finalizado", "cancelado", "arquivo_pendente"].includes(item.situacao);
                const informarData = ehPrestador && emAberto && podeInformarData(item);
                const confirmar = ehPrestador && emAberto;
                const analisar = item.situacao === "arquivo_pendente" && podeAnalisarArquivos(item);

                // O destaque (azul-marinho) segue o momento do agendamento: sem data,
                // combinar a data; antes da sessão, entrar na sala; passada a hora,
                // confirmar a realização. Evita destacar "entrar na sala" de algo que só
                // acontece semana que vem.
                //
                // As outras ações do profissional continuam VISÍVEIS como botão neutro —
                // nunca no menu. Confirmar a realização é o trabalho dele; esconder isso
                // num submenu é esconder a tarefa.
                const jaPassou = data ? new Date(item.data_agendamento) <= new Date() : false;
                const principal = informarData ? "data"
                    : (podeEntrarNaSala && !jaPassou) ? "sala"
                    : confirmar ? "confirmar"
                    : podeEntrarNaSala ? "sala"
                    : analisar ? "arquivos"
                    // nada pendente (encerrado, em geral): o card não fica com um menu
                    // órfão, oferece a consulta ao atendimento, que é o que resta fazer
                    : item.atendimento_uuid ? "atendimento"
                    : null;

                const classeAcao = (chave) => chave === principal ? "ag-card__acao" : "ag-card__acao--neutra";

                // no menu ficam só as ações acessórias
                const temMenu = [
                    emAberto && ehPrestador && mostrarFormularioDinamico(item),
                    kitPendente,
                    podeEditar(item),
                    principal !== "atendimento" && !!item.atendimento_uuid,
                ].some(Boolean);

                const classesCard = ["ag-card"];
                if (encerrado) classesCard.push("ag-card--encerrado");
                if (item.situacao === "cancelado") classesCard.push("ag-card--cancelado");

                return (
                    <div
                        key={item.id}
                        className={classesCard.join(" ")}
                        style={{ borderLeftColor: CORES_BARRA[item.situacao] || "#e6ebf2" }}
                    >
                        <div className="d-flex align-items-center justify-content-between" style={{ gap: "8px" }}>
                            <span className="ag-card__id">#{item.id}</span>
                            {/* encerrado usa a versão suave do badge: mantém a associação de
                                cor sem competir com a ação primária */}
                            <span className={`badge-agendamento ${encerrado ? "badge-ag-soft" : "badge-ag-pill"} badge-status-${item.situacao}`}>
                                {situacoesAgendamento[item.situacao] || item.situacao}
                            </span>
                        </div>

                        {data ? (
                            <div className="d-flex align-items-end justify-content-between" style={{ gap: "10px" }}>
                                <div className="d-flex align-items-baseline" style={{ gap: "7px" }}>
                                    <span className="ag-card__dia">{data.dia}</span>
                                    <span className="ag-card__mesano">{data.mes} {data.ano} · {data.semana}</span>
                                </div>
                                <span className="ag-card__hora">{data.hora}</span>
                            </div>
                        ) : (
                            <div>
                                <span className="ag-chip-sem-data">Data a combinar</span>
                            </div>
                        )}

                        <div className="d-flex flex-column" style={{ gap: "3px" }}>
                            <span className="ag-card__paciente">
                                {item.dados_beneficiario?.nome || item.dados_mentorado?.nome || "—"}
                            </span>
                            <span className="ag-card__servico">
                                {item.dados_servico_procedimento?.nome || "—"}
                            </span>
                        </div>

                        {!ehPrestador && (
                            item.dados_prestador?.nome
                                ? <div className="ag-meta">{item.dados_prestador.nome}</div>
                                : <div className="ag-meta ag-meta--alerta">Prestador não atribuído</div>
                        )}
                        {kit && <div className="ag-meta">{kit}</div>}

                        <div className="ag-card__rodape">
                            {informarData && (
                                <button type="button" className={classeAcao("data")} onClick={() => onInformarData(item)}>
                                    <FaRegCalendarAlt className="me-1" /> {ehAlteracaoDeData?.(item) ? "Alterar data" : "Informar data"}
                                </button>
                            )}
                            {podeEntrarNaSala && (
                                <a className={`${classeAcao("sala")} d-inline-flex align-items-center justify-content-center text-decoration-none`} href={`/meetings/view/${item.id}`}>
                                    <FaVideo className="me-1" /> Entrar na sala
                                </a>
                            )}
                            {confirmar && (
                                <button type="button" className={classeAcao("confirmar")} onClick={() => onConfirmarRealizacao(item)}>
                                    Confirmar realização
                                </button>
                            )}
                            {analisar && (
                                <button type="button" className={classeAcao("arquivos")} onClick={() => onArquivos(item)}>
                                    <FaFile className="me-1" /> Analisar arquivos
                                </button>
                            )}
                            {principal === "atendimento" && (
                                <a
                                    className={`${encerrado ? "ag-card__acao--recuada" : "ag-card__acao--neutra"} d-inline-flex align-items-center justify-content-center text-decoration-none`}
                                    href={`/atendimentos/edit/${item.atendimento_uuid}`}
                                >
                                    <FaEye className="me-1" /> Ver atendimento
                                </a>
                            )}

                            {/* link do paciente: ação de rotina antes da sessão, fica à mão */}
                            {sala && !encerrado && !ehBeneficiario && (
                                <AcoesLinkPaciente
                                    link={sala}
                                    agendamentoId={item.id}
                                    emailPaciente={item.dados_beneficiario?.email}
                                    compacto
                                    className="ag-card__icone"
                                />
                            )}

                            {temMenu && (
                                <Dropdown align="end">
                                    <Dropdown.Toggle
                                        as="button"
                                        type="button"
                                        className="ag-card__mais"
                                        id={`acoes-card-${item.id}`}
                                        title="Mais ações"
                                    >
                                        <FaChevronDown />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {emAberto && ehPrestador && mostrarFormularioDinamico(item) && (
                                            <Dropdown.Item onClick={() => onFormularioDinamico(item)}>
                                                <FaClipboardList className="me-2" /> {tituloFormularioDinamico(item)}
                                            </Dropdown.Item>
                                        )}
                                        {kitPendente && (
                                            <Dropdown.Item onClick={() => onConfirmarEnvioKit(item.atendimento_kit_id, item)}>
                                                <FaBoxOpen className="me-2" /> Confirmar envio do kit
                                            </Dropdown.Item>
                                        )}
                                        {podeEditar(item) && (
                                            <Dropdown.Item href={`/agendamentos/edit/${item.id}`}>
                                                <FaRegEdit className="me-2" /> Editar agendamento
                                            </Dropdown.Item>
                                        )}
                                        {principal !== "atendimento" && item.atendimento_uuid && (
                                            <Dropdown.Item href={`/atendimentos/edit/${item.atendimento_uuid}`}>
                                                <FaEye className="me-2" /> Ver atendimento
                                            </Dropdown.Item>
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default CardsAgendamentos;
